import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Switch from "@mui/material/Switch";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";

export type CreateClipsSettings = {
    clipCount: number;
    clipDurationSec: number;
    useLayout: boolean;
    layoutId: string | null;
    topic: string;
};

export type CreateClipLayoutOption = {
    id: string;
    name: string;
    eyebrow?: string;
    previewText?: string;
    accentColor?: string;
    previewUrl?: string;
    posterUrl?: string;
};

type CreateClipsSidebarProps = {
    onBack: () => void;
    onSubmit?: (settings: CreateClipsSettings) => void;
    isSubmitting?: boolean;
    onStopSubmitting?: () => void;
    layoutOptions?: CreateClipLayoutOption[];
};

const CLIP_COUNT_MIN = 1;
const CLIP_COUNT_MAX = 20;
const CLIP_DURATION_MIN = 10;
const CLIP_DURATION_MAX = 300;
const TOPIC_CHAR_LIMIT = 3000;
const TOPIC_MIN_HEIGHT = 56;
const TOPIC_MAX_HEIGHT = 160;
const LAYOUT_SCROLL_STEP = 264;

const DEFAULT_LAYOUT_OPTIONS: CreateClipLayoutOption[] = [
    {
        id: "social_clip_spotlight",
        name: "Social Clip - Spotlight",
        eyebrow: "HOOK FIRST",
        previewText: "Lead with the strongest line and keep captions anchored low.",
        accentColor: "#0B93D5",
    },
    {
        id: "social_clip_captions",
        name: "Social Clip - Captions",
        eyebrow: "BOLD TEXT",
        previewText: "Feature-heavy captions with a tighter talking-head crop.",
        accentColor: "#5B8CFF",
    },
    {
        id: "social_clip_clean",
        name: "Social Clip - Clean Frame",
        eyebrow: "MINIMAL",
        previewText: "Keep the frame quiet and let the speaker carry the pace.",
        accentColor: "#F59E0B",
    },
    {
        id: "social_clip_quote",
        name: "Social Clip - Quote Card",
        eyebrow: "QUOTE",
        previewText: "Mix a pull-quote layer with a centered portrait composition.",
        accentColor: "#7C3AED",
    },
    {
        id: "social_clip_punch_in",
        name: "Social Clip - Punch In",
        eyebrow: "FAST CUTS",
        previewText: "Use tighter reframes for momentum on every key sentence.",
        accentColor: "#EF4444",
    },
    {
        id: "social_clip_stack",
        name: "Social Clip - Stack",
        eyebrow: "STACKED",
        previewText: "Pair portrait video with floating copy and supporting callouts.",
        accentColor: "#10B981",
    },
    {
        id: "social_clip_reel",
        name: "Social Clip - Reel Cover",
        eyebrow: "REEL READY",
        previewText: "Push a title-forward opening for social feed previews.",
        accentColor: "#EC4899",
    },
    {
        id: "social_clip_podcast",
        name: "Social Clip - Podcast",
        eyebrow: "CONVERSATION",
        previewText: "Frame for interview-style beats and subtitle readability.",
        accentColor: "#14B8A6",
    },
];

const clampInteger = (value: number, min: number, max: number): number => {
    if (!Number.isFinite(value)) return min;
    return Math.min(max, Math.max(min, Math.round(value)));
};

const sanitizeIntegerInput = (value: string): string => String(value ?? "").replace(/[^\d]/g, "").slice(0, 3);

const normalizeIntegerInput = (raw: string, min: number, max: number, fallback: number): string => {
    const sanitized = sanitizeIntegerInput(raw);
    if (!sanitized) return String(clampInteger(fallback, min, max));
    return String(clampInteger(Number(sanitized), min, max));
};

const layoutSwitchSx = {
    width: 42,
    height: 26,
    p: 0,
    "& .MuiSwitch-switchBase": {
        p: "3px",
        transitionDuration: "150ms",
        "&.Mui-checked": {
            transform: "translateX(16px)",
            color: "#FFFFFF",
            "& + .MuiSwitch-track": {
                backgroundColor: "#4B3138",
                opacity: 1,
                borderColor: "#4B3138",
            },
        },
    },
    "& .MuiSwitch-thumb": {
        boxSizing: "border-box",
        width: 20,
        height: 20,
        boxShadow: "0 1px 2px rgba(17, 24, 39, 0.16)",
    },
    "& .MuiSwitch-track": {
        borderRadius: "999px",
        backgroundColor: "var(--app-border-muted)",
        border: "1px solid var(--app-border-muted)",
        opacity: 1,
    },
};

const cardSurfaceSx = {
    backgroundColor: "var(--app-bg-surface)",
    border: "1px solid var(--app-border-subtle)",
    borderRadius: "12px",
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
};

const placeholderGradient = (accentColor: string): string => {
    const accent = String(accentColor || "#0B93D5");
    return `linear-gradient(180deg, rgba(246, 236, 228, 0.92) 0%, rgba(232, 218, 208, 0.88) 38%, rgba(26, 23, 26, 0.98) 100%), radial-gradient(circle at 50% 10%, ${accent}33 0%, transparent 58%)`;
};

const NumericPillInput: React.FC<{
    value: string;
    onChange: (value: string) => void;
    onBlur: () => void;
    icon?: React.ReactNode;
    suffix?: string;
    ariaLabel: string;
}> = ({ value, onChange, onBlur, icon, suffix, ariaLabel }) => (
    <Box
        sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            minWidth: suffix ? 80 : 44,
            minHeight: 32,
            px: "10px",
            py: "4px",
            borderRadius: "8px",
            backgroundColor: "var(--app-bg-subtle)",
            border: "1px solid var(--app-border-subtle)",
        }}
    >
        {icon}
        <Box
            component="input"
            aria-label={ariaLabel}
            value={value}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
            onBlur={onBlur}
            inputMode="numeric"
            sx={{
                width: suffix ? 28 : 20,
                p: 0,
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--app-text-primary)",
                textAlign: "right",
                fontSize: 14,
                fontWeight: 500,
                fontFamily: "Inter, sans-serif",
                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                },
                appearance: "textfield",
            }}
        />
        {suffix ? (
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: "var(--app-text-muted)" }}>
                {suffix}
            </Typography>
        ) : null}
    </Box>
);

const CreateClipsSidebar: React.FC<CreateClipsSidebarProps> = ({
    onBack,
    onSubmit,
    isSubmitting,
    onStopSubmitting,
    layoutOptions,
}) => {
    const layouts = useMemo(
        () => (Array.isArray(layoutOptions) && layoutOptions.length > 0 ? layoutOptions : DEFAULT_LAYOUT_OPTIONS),
        [layoutOptions],
    );
    const [clipCountInput, setClipCountInput] = useState<string>("5");
    const [clipDurationInput, setClipDurationInput] = useState<string>("30");
    const [useLayout, setUseLayout] = useState<boolean>(true);
    const [selectedLayoutId, setSelectedLayoutId] = useState<string>(layouts[0]?.id ?? "");
    const [topic, setTopic] = useState<string>("");
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(layouts.length > 0);
    const [pendingDotCount, setPendingDotCount] = useState(0);
    const layoutScrollRef = useRef<HTMLDivElement | null>(null);
    const topicRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => {
        if (!layouts.some((layout) => layout.id === selectedLayoutId)) {
            setSelectedLayoutId(layouts[0]?.id ?? "");
        }
    }, [layouts, selectedLayoutId]);

    useLayoutEffect(() => {
        const textarea = topicRef.current;
        if (!textarea) return;
        textarea.style.height = "auto";
        const nextHeight = Math.min(TOPIC_MAX_HEIGHT, Math.max(TOPIC_MIN_HEIGHT, textarea.scrollHeight));
        textarea.style.height = `${nextHeight}px`;
        textarea.style.overflowY = textarea.scrollHeight > TOPIC_MAX_HEIGHT ? "auto" : "hidden";
    }, [topic]);

    useEffect(() => {
        const node = layoutScrollRef.current;
        if (!node) return undefined;

        const updateScrollState = () => {
            const maxScrollLeft = node.scrollWidth - node.clientWidth;
            setCanScrollLeft(node.scrollLeft > 4);
            setCanScrollRight(maxScrollLeft - node.scrollLeft > 4);
        };

        updateScrollState();
        node.addEventListener("scroll", updateScrollState);
        window.addEventListener("resize", updateScrollState);

        return () => {
            node.removeEventListener("scroll", updateScrollState);
            window.removeEventListener("resize", updateScrollState);
        };
    }, [layouts.length]);

    useEffect(() => {
        if (!isSubmitting) {
            setPendingDotCount(0);
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            setPendingDotCount((prev) => (prev + 1) % 4);
        }, 450);

        return () => window.clearInterval(intervalId);
    }, [isSubmitting]);

    const handleClipCountBlur = () => {
        setClipCountInput((prev) => normalizeIntegerInput(prev, CLIP_COUNT_MIN, CLIP_COUNT_MAX, 5));
    };

    const handleClipDurationBlur = () => {
        setClipDurationInput((prev) => normalizeIntegerInput(prev, CLIP_DURATION_MIN, CLIP_DURATION_MAX, 30));
    };

    const scrollLayouts = (direction: "left" | "right") => {
        const node = layoutScrollRef.current;
        if (!node) return;
        node.scrollBy({
            left: direction === "left" ? -LAYOUT_SCROLL_STEP : LAYOUT_SCROLL_STEP,
            behavior: "smooth",
        });
    };

    const handleSubmit = () => {
        const normalizedClipCount = clampInteger(
            Number(normalizeIntegerInput(clipCountInput, CLIP_COUNT_MIN, CLIP_COUNT_MAX, 5)),
            CLIP_COUNT_MIN,
            CLIP_COUNT_MAX,
        );
        const normalizedClipDuration = clampInteger(
            Number(normalizeIntegerInput(clipDurationInput, CLIP_DURATION_MIN, CLIP_DURATION_MAX, 30)),
            CLIP_DURATION_MIN,
            CLIP_DURATION_MAX,
        );
        const normalizedTopic = topic.slice(0, TOPIC_CHAR_LIMIT);

        setClipCountInput(String(normalizedClipCount));
        setClipDurationInput(String(normalizedClipDuration));
        setTopic(normalizedTopic);

        onSubmit?.({
            clipCount: normalizedClipCount,
            clipDurationSec: normalizedClipDuration,
            useLayout,
            layoutId: useLayout ? (selectedLayoutId || layouts[0]?.id || null) : null,
            topic: normalizedTopic.trim(),
        });
    };

    const handlePendingStop = () => {
        onStopSubmitting?.();
    };

    const pendingLabel = `Preparing clips${".".repeat(pendingDotCount)}`;

    return (
        <Box
            data-tour="create-clips-panel"
            sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                p: "3px",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginLeft: "8px" }}>
                <IconButton size="small" onClick={isSubmitting ? handlePendingStop : onBack} sx={{ p: 0.5 }}>
                    <ArrowBackRoundedIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "var(--app-text-primary)" }}>
                    Create clips
                </Typography>
            </Box>

            {isSubmitting ? (
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: "18px",
                        overflowY: "auto",
                        px: "12px",
                        pt: "28px",
                        pb: "10px",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: "14px" }}>
                        <CircularProgress
                            size={24}
                            thickness={4.2}
                            sx={{
                                color: "#0B93D5",
                                flexShrink: 0,
                                mt: "2px",
                            }}
                        />
                        <Box aria-live="polite">
                            <Typography sx={{ fontSize: 15, fontWeight: 500, color: "var(--app-text-primary)" }}>
                                {pendingLabel}
                            </Typography>
                            <Typography sx={{ fontSize: 13, lineHeight: 1.55, color: "var(--app-text-muted)", mt: "2px" }}>
                                Finding the best moments and preparing your clip compositions.
                            </Typography>
                        </Box>
                    </Box>

                    <Button
                        onClick={handlePendingStop}
                        startIcon={<StopRoundedIcon sx={{ fontSize: 15 }} />}
                        sx={{
                            alignSelf: "flex-start",
                            textTransform: "none",
                            color: "var(--app-text-primary)",
                            fontSize: 13,
                            fontWeight: 500,
                            borderRadius: "8px",
                            minHeight: 34,
                            px: "8px",
                            "&:hover": {
                                backgroundColor: "rgba(17, 24, 39, 0.04)",
                            },
                        }}
                    >
                        Stop
                    </Button>
                </Box>
            ) : (
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: "auto",
                        pr: "2px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        scrollbarWidth: "thin",
                    }}
                >
                    <Box sx={{ px: "8px" }}>
                        <Typography sx={{ fontSize: 13, lineHeight: 1.55, color: "var(--app-text-muted)" }}>
                            Picks your most viral-worthy moments and creates clips that pop.
                        </Typography>
                    </Box>

                    <Box sx={cardSurfaceSx}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "12px",
                            }}
                        >
                            <Typography sx={{ fontSize: 13, fontWeight: 500, color: "var(--app-text-primary)" }}>
                                Number of clips
                            </Typography>
                            <NumericPillInput
                                value={clipCountInput}
                                onChange={(value) => setClipCountInput(sanitizeIntegerInput(value))}
                                onBlur={handleClipCountBlur}
                                ariaLabel="Number of clips"
                            />
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "12px",
                            }}
                        >
                            <Typography sx={{ fontSize: 13, fontWeight: 500, color: "var(--app-text-primary)" }}>
                                Clip duration
                            </Typography>
                            <NumericPillInput
                                value={clipDurationInput}
                                onChange={(value) => setClipDurationInput(sanitizeIntegerInput(value))}
                                onBlur={handleClipDurationBlur}
                                icon={<TimerOutlinedIcon sx={{ fontSize: 15, color: "var(--app-text-muted)" }} />}
                                suffix="s"
                                ariaLabel="Clip duration in seconds"
                            />
                        </Box>
                    </Box>

                    {/* Layout panel — hidden for now
                    <Box sx={cardSurfaceSx}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "var(--app-text-primary)" }}>
                                Layout
                            </Typography>
                            <Switch
                                checked={useLayout}
                                onChange={(_, checked) => setUseLayout(checked)}
                                inputProps={{ "aria-label": "Enable layout selection" }}
                                sx={layoutSwitchSx}
                            />
                        </Box>

                        <Box
                            sx={{
                                position: "relative",
                                opacity: useLayout ? 1 : 0.56,
                                transition: "opacity 0.15s ease",
                            }}
                        >
                            {canScrollLeft ? (
                                <IconButton
                                    size="small"
                                    onClick={() => scrollLayouts("left")}
                                    disabled={!useLayout}
                                    sx={{
                                        position: "absolute",
                                        left: 4,
                                        top: 74,
                                        zIndex: 2,
                                        width: 34,
                                        height: 34,
                                        backgroundColor: "rgba(255, 255, 255, 0.94)",
                                        boxShadow: "0 8px 18px rgba(17, 24, 39, 0.12)",
                                        "&:hover": {
                                            backgroundColor: "#FFFFFF",
                                        },
                                    }}
                                >
                                    <ChevronLeftRoundedIcon />
                                </IconButton>
                            ) : null}

                            <Box
                                ref={layoutScrollRef}
                                sx={{
                                    display: "flex",
                                    gap: "12px",
                                    overflowX: "auto",
                                    pb: "2px",
                                    pr: canScrollRight ? "44px" : 0,
                                    pl: canScrollLeft ? "44px" : 0,
                                    scrollSnapType: "x mandatory",
                                    scrollbarWidth: "none",
                                    "&::-webkit-scrollbar": {
                                        display: "none",
                                    },
                                }}
                            >
                                {layouts.map((layout) => {
                                    const isSelected = layout.id === selectedLayoutId;
                                    return (
                                        <Box
                                            key={layout.id}
                                            sx={{
                                                flex: "0 0 120px",
                                                minWidth: 120,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: "8px",
                                                scrollSnapAlign: "start",
                                            }}
                                        >
                                            <Box
                                                role="button"
                                                tabIndex={useLayout ? 0 : -1}
                                                onClick={() => {
                                                    if (!useLayout) return;
                                                    setSelectedLayoutId(layout.id);
                                                }}
                                                onKeyDown={(event) => {
                                                    if (!useLayout) return;
                                                    if (event.key === "Enter" || event.key === " ") {
                                                        event.preventDefault();
                                                        setSelectedLayoutId(layout.id);
                                                    }
                                                }}
                                                sx={{
                                                    position: "relative",
                                                    height: 168,
                                                    borderRadius: "10px",
                                                    p: "3px",
                                                    border: isSelected ? "2px solid #0B93D5" : "1px solid var(--app-border-subtle)",
                                                    backgroundColor: isSelected ? "rgba(11, 147, 213, 0.08)" : "var(--app-bg-subtle)",
                                                    boxShadow: isSelected ? "0 12px 22px rgba(11, 147, 213, 0.14)" : "none",
                                                    cursor: useLayout ? "pointer" : "default",
                                                    transition: "all 0.15s ease",
                                                    outline: "none",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: "relative",
                                                        width: "100%",
                                                        height: "100%",
                                                        borderRadius: "10px",
                                                        overflow: "hidden",
                                                        backgroundImage: layout.posterUrl ? `url(${layout.posterUrl})` : placeholderGradient(layout.accentColor ?? "#0B93D5"),
                                                        backgroundSize: "cover",
                                                        backgroundPosition: "center",
                                                    }}
                                                >
                                                    {layout.previewUrl ? (
                                                        <Box
                                                            component="video"
                                                            src={layout.previewUrl}
                                                            poster={layout.posterUrl}
                                                            muted
                                                            autoPlay
                                                            loop
                                                            playsInline
                                                            preload="metadata"
                                                            sx={{
                                                                position: "absolute",
                                                                inset: 0,
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "cover",
                                                            }}
                                                        />
                                                    ) : null}

                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            top: "16%",
                                                            left: "50%",
                                                            transform: "translateX(-50%)",
                                                            width: "36%",
                                                            aspectRatio: "1 / 1",
                                                            borderRadius: "50%",
                                                            backgroundColor: "rgba(7, 10, 15, 0.94)",
                                                            boxShadow: "0 10px 24px rgba(0, 0, 0, 0.22)",
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            bottom: 0,
                                                            left: "50%",
                                                            transform: "translateX(-50%)",
                                                            width: "70%",
                                                            height: "68%",
                                                            borderRadius: "40% 40% 12% 12%",
                                                            backgroundColor: "rgba(7, 10, 15, 0.96)",
                                                            boxShadow: "0 14px 28px rgba(0, 0, 0, 0.22)",
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            inset: 0,
                                                            background: "linear-gradient(180deg, rgba(255,255,255,0) 48%, rgba(10, 10, 10, 0.82) 86%, rgba(10, 10, 10, 0.96) 100%)",
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            left: 10,
                                                            right: 10,
                                                            bottom: 10,
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: "6px",
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: 10.5,
                                                                fontWeight: 700,
                                                                color: layout.accentColor ?? "#93C5FD",
                                                                letterSpacing: "0.08em",
                                                            }}
                                                        >
                                                            {layout.eyebrow ?? "SOCIAL"}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 11.5,
                                                                lineHeight: 1.35,
                                                                color: "#F8FAFC",
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 3,
                                                                WebkitBoxOrient: "vertical",
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            {layout.previewText ?? "Portrait clip preview"}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    fontWeight: isSelected ? 600 : 500,
                                                    color: "var(--app-text-primary)",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                                title={layout.name}
                                            >
                                                {layout.name}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>

                            {canScrollRight ? (
                                <IconButton
                                    size="small"
                                    onClick={() => scrollLayouts("right")}
                                    disabled={!useLayout}
                                    sx={{
                                        position: "absolute",
                                        right: 4,
                                        top: 74,
                                        zIndex: 2,
                                        width: 42,
                                        height: 42,
                                        backgroundColor: "rgba(255, 255, 255, 0.94)",
                                        boxShadow: "0 8px 18px rgba(17, 24, 39, 0.14)",
                                        "&:hover": {
                                            backgroundColor: "#FFFFFF",
                                        },
                                    }}
                                >
                                    <ChevronRightRoundedIcon />
                                </IconButton>
                            ) : null}
                        </Box>
                    </Box>
                    */}

                    <Box sx={cardSurfaceSx}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "var(--app-text-primary)" }}>
                                Topic <Box component="span" sx={{ fontWeight: 400, color: "var(--app-text-muted)" }}>(optional)</Box>
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => setTopic("")}
                                disabled={!topic}
                                sx={{
                                    width: 26,
                                    height: 26,
                                    color: topic ? "var(--app-text-muted)" : "var(--app-text-subtle)",
                                }}
                            >
                                <CloseRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Box>

                        <Box
                            sx={{
                                borderRadius: "10px",
                                backgroundColor: "var(--app-bg-subtle)",
                                border: "1px solid var(--app-border-subtle)",
                                px: "10px",
                                py: "10px",
                            }}
                        >
                            <Box
                                component="textarea"
                                ref={topicRef}
                                value={topic}
                                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                                    setTopic(event.target.value.slice(0, TOPIC_CHAR_LIMIT))
                                }
                                placeholder="Describe the goal and criteria to find the best clips..."
                                sx={{
                                    width: "100%",
                                    minHeight: `${TOPIC_MIN_HEIGHT}px`,
                                    maxHeight: `${TOPIC_MAX_HEIGHT}px`,
                                    p: 0,
                                    resize: "none",
                                    border: "none",
                                    outline: "none",
                                    background: "transparent",
                                    color: "var(--app-text-primary)",
                                    fontSize: 13,
                                    lineHeight: 1.5,
                                    fontFamily: "Inter, sans-serif",
                                    overflowY: "hidden",
                                    "&::placeholder": {
                                        color: "var(--app-text-muted)",
                                        opacity: 1,
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    <Button
                        data-tour="create-clips-submit"
                        fullWidth
                        onClick={handleSubmit}
                        sx={{
                            fontFamily: "Inter",
                            fontSize: "14px",
                            fontWeight: 500,
                            textTransform: "none",
                            color: "#FFFFFF",
                            backgroundColor: "#1A1A1A",
                            borderRadius: "8px",
                            height: "40px",
                            "&:hover": {
                                backgroundColor: "#2B2B2B",
                            },
                        }}
                    >
                        Submit
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default CreateClipsSidebar;
